import { Plus, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { Column } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { DeleteReasonDialog } from "@/components/shared/delete-reason-dialog";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useDeleteRiderSale, useVoidRiderSale } from "@/hooks/use-rider-sales";
import { useRiders } from "@/hooks/use-riders";
import { useServerTable } from "@/hooks/use-server-table";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { queryKeys } from "@/lib/query-keys";
import { EntityActivityLogDialog } from "@/pages/activity-logs/entity-activity-log-dialog";
import { riderSaleService } from "@/services/rider-sale";

import { RiderSaleReportPdfButton } from "./pdf/rider-sale-report-pdf-button";
import { RiderSaleDetailDialog } from "./rider-sale-detail-dialog";
import { RiderSaleDialog } from "./rider-sale-dialog";
import {
  RIDER_SALE_PAYMENT_METHOD_LABELS,
  RIDER_SALE_STATUS_BADGE_VARIANT,
  RIDER_SALE_STATUS_LABELS,
} from "./rider-sale-labels";
import { RiderSaleMobileCard } from "./rider-sale-mobile-card";
import { RiderSaleRowActions } from "./rider-sale-row-actions";

import type { RiderSale } from "@artisancode/api-types";

export function RiderSaleList() {
  const canCreate = useHasPermission("rider_sales.create");
  const canUpdate = useHasPermission("rider_sales.update");
  const canDelete = useHasPermission("rider_sales.delete");
  const canVoid = useHasPermission("rider_sales.void");

  const { mutate: deleteRiderSale, isPending: isDeleting } =
    useDeleteRiderSale();
  const { mutate: voidRiderSale, isPending: isVoiding } = useVoidRiderSale();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RiderSale | null>(null);
  const [deleting, setDeleting] = useState<RiderSale | null>(null);
  const [voiding, setVoiding] = useState<RiderSale | null>(null);
  const [detailSale, setDetailSale] = useState<RiderSale | null>(null);
  const [historySaleId, setHistorySaleId] = useState<string | null>(null);

  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<RiderSale>({
    queryKey: (params) => queryKeys.riderSales.list(params),
    fetcher: (params) => riderSaleService.list(params),
    pageSize: 10,
    filterKeys: ["rider_id", "date_from", "date_to", "workspace_id"],
  });

  const { data: ridersData } = useRiders({ per_page: 100 });
  const riderOptions = (ridersData?.items ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  function handleDeleteConfirm(reason: string) {
    if (!deleting) return;
    deleteRiderSale(
      { id: deleting.id, reason },
      {
        onSuccess: () => {
          toast.success("Penjualan rider berhasil dihapus.");
          setDeleting(null);
        },
        onError: () => toast.error("Gagal menghapus penjualan rider."),
      },
    );
  }

  function handleVoidConfirm(reason: string) {
    if (!voiding) return;
    voidRiderSale(
      { id: voiding.id, reason },
      {
        onSuccess: () => {
          toast.success("Penjualan rider berhasil dibatalkan (void).");
          setVoiding(null);
        },
        onError: () => toast.error("Gagal membatalkan penjualan rider."),
      },
    );
  }

  const columns: Column<RiderSale>[] = [
    {
      key: "date",
      label: "Tanggal / Rider",
      render: (r) => (
        <div className="flex flex-col">
          <span>{r.date}</span>
          <span className="text-xs text-muted-foreground">{r.riderName}</span>
        </div>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (r: RiderSale) => (
              <span className="text-sm">{workspaceName(r.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "totals",
      label: "Unit Terjual / Sisa",
      render: (r) => {
        const totalDiscrepancy = r.lines.reduce(
          (sum, l) => sum + (l.discrepancyQty ?? 0),
          0,
        );
        return (
          <div className="flex flex-col items-start gap-1 text-xs">
            <span>
              Terjual: {r.totalSoldQty} (sisa {r.totalRemainingQty})
            </span>
            <span className="text-muted-foreground">
              Ambil: {r.totalDispatchedQty}
            </span>
            {totalDiscrepancy > 0 && (
              <Badge variant="destructive" className="h-4 px-1 text-[10px]">
                Selisih: {totalDiscrepancy} unit
              </Badge>
            )}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => setDetailSale(r)}
            >
              Detail penjualan
            </Button>
          </div>
        );
      },
    },
    {
      key: "setoran",
      label: "Total Setoran",
      render: (r) => (
        <span className="font-medium">
          Rp {(r.totalRevenue ?? 0).toLocaleString("id-ID")}
        </span>
      ),
    },
    {
      key: "bonus",
      label: "Bonus",
      render: (r) => (
        <span>Rp {(r.totalBonus ?? 0).toLocaleString("id-ID")}</span>
      ),
    },
    {
      key: "pemasukan",
      label: "Pembayaran",
      render: (r) => {
        const selisih = (r.totalRevenue ?? 0) - r.totalCollected;
        return (
          <div className="flex items-center gap-1.5 text-xs">
            {r.payments.length === 0 ? (
              <span className="text-muted-foreground">Belum ada</span>
            ) : (
              <div className="flex flex-col">
                {r.payments.map((p) => (
                  <span key={p.method}>
                    {RIDER_SALE_PAYMENT_METHOD_LABELS[p.method]}:{" "}
                    {p.amount.toLocaleString("id-ID")}
                  </span>
                ))}
              </div>
            )}
            {selisih !== 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 text-amber-500 hover:text-amber-600"
                  >
                    <TriangleAlert className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3 text-xs">
                  Total Setoran Rp{" "}
                  {(r.totalRevenue ?? 0).toLocaleString("id-ID")}, tapi
                  pembayaran yang dicatat baru Rp{" "}
                  {r.totalCollected.toLocaleString("id-ID")}. Selisih Rp{" "}
                  {Math.abs(selisih).toLocaleString("id-ID")}{" "}
                  {selisih > 0 ? "belum" : "melebihi"} tercatat di Kas — pas-kan
                  metode pembayaran dengan Total Setoran.
                </PopoverContent>
              </Popover>
            )}
          </div>
        );
      },
    },

    {
      key: "status",
      label: "Status",
      render: (r) => (
        <div className="flex flex-col gap-1">
          <Badge variant={RIDER_SALE_STATUS_BADGE_VARIANT[r.status]}>
            {RIDER_SALE_STATUS_LABELS[r.status]}
          </Badge>
          {r.status === "cancelled" && r.cancelReason && (
            <span className="text-xs text-muted-foreground italic">
              &ldquo;{r.cancelReason}&rdquo;
            </span>
          )}
        </div>
      ),
    },
  ];

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: RiderSale) {
    setEditing(item);
    setOpen(true);
  }

  return (
    <div>
      <PageHeader
        title="Penjualan Rider"
        description="Input ambil/jual stok harian per rider."
        action={
          <div className="flex items-center gap-2">
            {table.activeFilters.date_from && table.activeFilters.date_to && (
              <RiderSaleReportPdfButton
                dateFrom={table.activeFilters.date_from}
                dateTo={table.activeFilters.date_to}
                workspaceId={
                  table.activeFilters.workspace_id || me?.workspaceId
                }
                workspaceName={
                  workspaceName(
                    table.activeFilters.workspace_id || me?.workspaceId || "",
                  ) !== "-"
                    ? workspaceName(
                        table.activeFilters.workspace_id ||
                          me?.workspaceId ||
                          "",
                      )
                    : undefined
                }
                riderId={table.activeFilters.rider_id}
              />
            )}
            <Button size="sm" onClick={openAdd} disabled={!canCreate}>
              <Plus className="mr-1 h-4 w-4" />
              Tambah
            </Button>
          </div>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        filters={[
          {
            key: "rider_id",
            label: "Rider",
            options: riderOptions,
            searchable: true,
          },
          ...workspaceFilter,
        ]}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        dateRange={{
          from: table.activeFilters.date_from ?? "",
          to: table.activeFilters.date_to ?? "",
          onChange: (date_from, date_to) =>
            table.onFiltersChange({ date_from, date_to }),
        }}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        loading={table.loading}
        actions={(item) => (
          <RiderSaleRowActions
            item={item}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canVoid={canVoid}
            onEdit={openEdit}
            onDelete={setDeleting}
            onVoid={setVoiding}
            onHistory={(s) => setHistorySaleId(s.id)}
          />
        )}
        mobileRender={(r) => (
          <RiderSaleMobileCard
            sale={r}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canVoid={canVoid}
            onEdit={openEdit}
            onDelete={setDeleting}
            onVoid={setVoiding}
            onShowDetail={setDetailSale}
            onHistory={(s) => setHistorySaleId(s.id)}
          />
        )}
      />

      <RiderSaleDialog open={open} onOpenChange={setOpen} editing={editing} />
      <EntityActivityLogDialog
        entityName="rider_sale"
        entityId={historySaleId}
        open={historySaleId !== null}
        onOpenChange={(next) => !next && setHistorySaleId(null)}
      />
      <DeleteReasonDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && setDeleting(null)}
        title="Hapus Penjualan Rider"
        description={
          deleting
            ? `Penjualan rider "${deleting.riderName}" (${deleting.date}) akan dihapus.`
            : undefined
        }
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
      <DeleteReasonDialog
        open={voiding !== null}
        onOpenChange={(next) => !next && setVoiding(null)}
        title="Void / Batalkan Penjualan Rider"
        description={
          voiding
            ? `Penjualan rider "${voiding.riderName}" (${voiding.date}) akan dibatalkan. Transaksi tetap tercatat dengan status Dibatalkan.`
            : undefined
        }
        onConfirm={handleVoidConfirm}
        loading={isVoiding}
        fieldLabel="Alasan void"
        placeholder="Jelaskan alasan pembatalan transaksi..."
        confirmLabel="Void"
      />

      <RiderSaleDetailDialog
        sale={detailSale}
        onOpenChange={(next) => !next && setDetailSale(null)}
      />
    </div>
  );
}
